import { useTranslation } from "react-i18next";
import { TourRoute } from "@/types/tour";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Share2, MapPin } from "lucide-react";
import { toast } from "sonner";

interface ShareRouteProps {
  route: TourRoute;
}

function buildMapsUrl(route: TourRoute): string {
  const entries = route.schedule;
  if (entries.length === 0) return "https://maps.google.com";

  const last = entries[entries.length - 1];
  const destination = `${last.bar.lat},${last.bar.lng}`;
  const destinationPlaceId = last.bar.placeId;

  // Origin intentionally left blank so Google Maps uses the user's current location.
  // All bars (including the first) become waypoints; the last bar is the destination.
  const waypointEntries = entries.slice(0, -1);
  const waypoints = waypointEntries
    .map((s) => `${s.bar.lat},${s.bar.lng}`)
    .join("|");
  const waypointPlaceIds = waypointEntries
    .map((s) => s.bar.placeId)
    .filter(Boolean)
    .join("|");

  const params = new URLSearchParams({
    api: "1",
    origin: "",
    destination,
    travelmode: "walking",
  });
  if (destinationPlaceId) params.set("destination_place_id", destinationPlaceId);
  if (waypoints) params.set("waypoints", waypoints);
  if (waypointPlaceIds) params.set("waypoint_place_ids", waypointPlaceIds);
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

export function ShareRoute({ route }: ShareRouteProps) {
  const { t } = useTranslation();

  const handleMapsLink = async () => {
    const url = buildMapsUrl(route);
    try {
      await navigator.clipboard?.writeText(url);
    } catch {
      // ignore
    }
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success(t("share.copied"));
  };

  return (
    <Card className="border-border/50 bg-card p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-3">
        <Share2 className="h-4 w-4 text-primary" />
        <h3 className="font-semibold">{t("share.title")}</h3>
      </div>
      <Button variant="outline" onClick={handleMapsLink} className="gap-2 w-full sm:w-auto">
        <MapPin className="h-4 w-4" /> {t("share.maps_link")}
      </Button>
    </Card>
  );
}
